import React, { useCallback, useEffect, useState } from "react";
import { FiPrinter } from "react-icons/fi";
import api from "../api";
import { fmtCLP, fmtFecha, formatPagos } from "../utils/formato";
import { imprimirTicketCierre } from "../utils/imprimirTicketCaja";
import "./CierresCaja.css";

const N = (v) => Number(v || 0);
const comandaPedido = (p) => p?.numero_comanda ?? p?.numeroComanda ?? p?.id ?? "—";
const diferenciaClass = (v) => N(v) === 0 ? "ok" : N(v) > 0 ? "sobrante" : "faltante";

export default function CierresCaja({ usuario }) {
  const [cierres, setCierres] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [detallePedidos, setDetallePedidos] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        setError(null);
        const params = {};
        if (usuario?.rol !== "superadmin" && usuario?.id) {
          params.usuario_id = usuario.id;
        }
        const { data } = await api.get("/api/caja/cierres", { params });
        setCierres(data?.rows || []);
      } catch (e) {
        setError(e?.response?.data?.error || e.message || "Error al cargar cierres");
      } finally {
        setCargando(false);
      }
    })();
  }, [usuario?.id, usuario?.rol]);

  const toggleDetalle = useCallback(async (c) => {
    const key = `${c.usuario_id}-${c.hasta}`;
    if (expandido === key) {
      setExpandido(null);
      setDetallePedidos([]);
      return;
    }
    setExpandido(key);
    setCargandoDetalle(true);
    setDetallePedidos([]);
    try {
      const desde = typeof c.desde === "string" ? c.desde : new Date(c.desde).toISOString();
      const hasta = typeof c.hasta === "string" ? c.hasta : new Date(c.hasta).toISOString();
      const { data } = await api.get("/api/caja/cierres/detalle", {
        params: { usuario_id: c.usuario_id, desde, hasta },
      });
      setDetallePedidos(data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Error al cargar detalle");
      setExpandido(null);
    } finally {
      setCargandoDetalle(false);
    }
  }, [expandido]);

  const reimprimirCierre = useCallback((c) => {
    imprimirTicketCierre({
      nombreCajero: c.usuario_nombre || usuario?.nombre || `Usuario ${c.usuario_id}`,
      cierre: c,
      resumen: {
        total_pedidos: c.total_pedidos,
        total_neto: c.total_neto,
        total_efectivo: c.total_efectivo,
        total_tarjeta: c.total_tarjeta,
        total_transfer: c.total_transfer,
        total_otro: c.total_otro,
      },
      desgloseEfectivo: c.desglose_efectivo || null,
      movimientos: [],
      apertura: null,
    });
  }, [usuario?.nombre]);

  return (
    <div className="cc-page">
      <header className="cc-header">
        <h1>Cierres de caja</h1>
        <p className="cc-desc">
          {usuario?.rol === "superadmin"
            ? "Historial de todos los cierres de caja."
            : "Tus cierres de caja."}
        </p>
      </header>

      {error && (
        <div className="cc-alert error">{error}</div>
      )}

      {cargando ? (
        <div className="cc-empty">Cargando cierres…</div>
      ) : !cierres.length ? (
        <div className="cc-empty">No hay cierres de caja.</div>
      ) : (
        <div className="cc-table-wrap">
          <table className="cc-table">
            <thead>
              <tr>
                <th></th>
                <th>Período</th>
                {usuario?.rol === "superadmin" && <th>Usuario</th>}
                <th className="center">Pedidos</th>
                <th className="right">Total neto</th>
                <th className="right">Efectivo</th>
                <th className="right">Contado</th>
                <th className="right">Dif.</th>
                <th className="right">Tarjeta</th>
                <th className="right">Transfer.</th>
                <th className="right">Otro</th>
                <th className="center">Acción</th>
              </tr>
            </thead>
            <tbody>
              {cierres.map((c) => {
                const key = `${c.usuario_id}-${c.hasta}`;
                const isOpen = expandido === key;
                const totalNetoPedido = (p) => N(p.total_neto ?? p.total);
                const subtotalPedido = (p) =>
                  (p.items || []).reduce((s, i) => s + N(i.precio) * N(i.cantidad), 0);
                return (
                  <React.Fragment key={key}>
                    <tr className={isOpen ? "cc-row-open" : ""}>
                      <td className="cc-cell-action">
                        <button
                          type="button"
                          className="cc-btn-expand"
                          onClick={() => toggleDetalle(c)}
                          aria-expanded={isOpen}
                          title={isOpen ? "Ocultar detalle" : "Ver detalle de ventas"}
                        >
                          {isOpen ? "−" : "+"}
                        </button>
                      </td>
                      <td>
                        <span className="cc-periodo">
                          {fmtFecha(c.desde)} — {fmtFecha(c.hasta)}
                        </span>
                      </td>
                      {usuario?.rol === "superadmin" && (
                        <td>{c.usuario_nombre ?? `ID ${c.usuario_id}`}</td>
                      )}
                      <td className="center">{c.total_pedidos ?? 0}</td>
                      <td className="right">{fmtCLP(c.total_neto)}</td>
                      <td className="right">{fmtCLP(c.total_efectivo)}</td>
                      <td className="right">{c.efectivo_contado != null ? fmtCLP(c.efectivo_contado) : "—"}</td>
                      <td className={`right cc-diff ${diferenciaClass(c.diferencia_efectivo)}`}>
                        {c.diferencia_efectivo != null ? fmtCLP(c.diferencia_efectivo) : "—"}
                      </td>
                      <td className="right">{fmtCLP(c.total_tarjeta)}</td>
                      <td className="right">{fmtCLP(c.total_transfer)}</td>
                      <td className="right">{fmtCLP(c.total_otro)}</td>
                      <td className="center">
                        <button
                          type="button"
                          className="cc-btn-reprint"
                          onClick={() => reimprimirCierre(c)}
                          title="Reimprimir cierre"
                          aria-label="Reimprimir cierre"
                        >
                          <FiPrinter aria-hidden="true" />
                          <span>Reimprimir</span>
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="cc-row-expand">
                        <td colSpan={usuario?.rol === "superadmin" ? 12 : 11}>
                          <div className="cc-detalle">
                            {cargandoDetalle ? (
                              <p className="cc-detalle-loading">Cargando ventas…</p>
                            ) : !detallePedidos.length ? (
                              <p className="cc-detalle-empty">No hay pedidos en este período.</p>
                            ) : (
                              <>
                                <table className="cc-detalle-table">
                                  <thead>
                                    <tr>
                                      <th>Comanda</th>
                                      <th>Fecha / Hora</th>
                                      <th>Cliente</th>
                                      <th>Medio pago</th>
                                      <th className="right">Total neto</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detallePedidos.map((p) => (
                                      <tr key={p.id}>
                                        <td>{comandaPedido(p)}</td>
                                        <td>{fmtFecha(p.creado_en)}</td>
                                        <td>{p.cliente || "—"}</td>
                                        <td>{formatPagos(p.pagos, p.medio_pago || "—")}</td>
                                        <td className="right">{fmtCLP(totalNetoPedido(p))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div className="cc-detalle-items">
                                  <h4>Ítems por pedido</h4>
                                  {detallePedidos.map((p) => (
                                    <div key={p.id} className="cc-pedido-block">
                                      <strong>Comanda #{comandaPedido(p)}</strong> — {p.cliente || "Sin nombre"} — {fmtCLP(totalNetoPedido(p))}
                                      {N(p.descuento) > 0 && (
                                        <div>
                                          Subtotal {fmtCLP(subtotalPedido(p))} - descuento {fmtCLP(p.descuento)} = {fmtCLP(totalNetoPedido(p))}
                                        </div>
                                      )}
                                      <ul className="cc-items-list">
                                        {(p.items || []).map((i, idx) => (
                                          <li key={idx}>
                                            {i.nombre} x{N(i.cantidad)} — {fmtCLP(N(i.precio) * N(i.cantidad))}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
